import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { firestore } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

/* ---------- Layout ---------- */

const Page = styled.main`
  min-height: 70vh;
  padding: 40px 18px;
  background: #f7fafc;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Hero = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 22px;
  margin-bottom: 18px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(26px, 3vw, 44px);
  color: #111827;
  font-weight: 900;
  letter-spacing: 0.3px;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: #4b5563;
  line-height: 1.6;
  max-width: 820px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const BtnGhost = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 900;
  text-decoration: none;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }
`;

/* ---------- Cards grid ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;

  @media (min-width: 900px) {
    grid-template-columns: 1.25fr 0.75fr;
    align-items: start;
  }
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;

  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const CardTitle = styled.div`
  font-weight: 900;
  color: #111827;
`;

const Badge = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.1);
  border: 1px solid rgba(15, 118, 110, 0.18);
  padding: 6px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

const Body = styled.div`
  padding: 18px;
`;

const Text = styled.div`
  color: #374151;
  line-height: 1.7;
  font-size: 15px;

  p { margin: 0 0 10px; }
  ul { margin: 10px 0 12px 20px; }
  li { margin: 6px 0; }
`;

const Html = styled.div`
  color: #374151;
  line-height: 1.7;
  font-size: 15px;

  p { margin: 0 0 10px; }
  ul, ol { margin: 10px 0 12px 20px; }
  li { margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; margin: 10px 0; }
  th, td { padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: left; }
`;

const Skeleton = styled.div`
  height: 180px;
  border-radius: 14px;
  background: linear-gradient(90deg, #f3f4f6, #fafafa, #f3f4f6);
  background-size: 200% 100%;
  animation: shimmer 1.1s infinite linear;

  @keyframes shimmer {
    0% { background-position: 0% 0; }
    100% { background-position: 200% 0; }
  }
`;

/* ---------- Toggle buttons ---------- */

const ToggleRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const ToggleBtn = styled.button`
  appearance: none;
  border-radius: 999px;
  padding: 10px 14px;
  font-weight: 900;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "#0f766e" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#111827")};

  &:hover {
    background: ${({ $active }) => ($active ? "#0f766e" : "#f9fafb")};
  }
`;

/* ---------- Opening hours list UI ---------- */

const HoursList = styled.div`
  display: grid;
  gap: 10px;

  @media (min-width: 650px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const HoursRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #fafafa;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HoursRowToday = styled(HoursRow)`
  background: rgba(15, 118, 110, 0.1);
  border-color: rgba(15, 118, 110, 0.25);
`;

const Day = styled.div`
  font-weight: 900;
  color: #111827;
`;

const Time = styled.div`
  font-weight: 900;
  color: #111827;
`;

const TodayBadge = styled.span`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 900;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.15);
  border-radius: 999px;
  padding: 4px 10px;
`;

/* ---------- Helpers ---------- */

function sanitizeHtmlMinimally(input) {
  if (!input) return "";
  let out = String(input);
  out = out.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  out = out.replace(/\son\w+="[^"]*"/gi, "");
  out = out.replace(/\son\w+='[^']*'/gi, "");
  out = out.replace(/href\s*=\s*["']\s*javascript:[^"']*["']/gi, 'href="#"');
  return out;
}

const sanitize = (v) => String(v ?? "").trim().replace(/^"+|"+$/g, "");

// Klarar både:
// "Mån–Fre: 10–18", "Lör: 11–16", "Mån 10–20", "Sön Stängt"
const extractRows = (text = "") => {
  const cleaned = sanitize(text)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/\r/g, "")
    .trim();

  if (!cleaned) return [];

  const lines = cleaned
    .split("\n")
    .map((r) => r.trim())
    .filter(Boolean);

  const timeLike = /(\d{1,2}[:.]\d{2}|\d{1,2})\s*[–-]\s*(\d{1,2}[:.]\d{2}|\d{1,2})/;
  const dayWords = /(mån|tis|ons|tor|fre|lör|sön)/i;

  return lines
    .map((line) => {
      // "Dag: Tid" eller "Dag - Tid"
      let m = line.match(/^(.+?)(?:\s*[:\-]\s*)(.+)$/);
      if (m) return { day: m[1].trim(), time: m[2].trim() };

      // "Mån 10–20"
      const idx = line.search(timeLike);
      if (idx > 0 && dayWords.test(line.slice(0, idx))) {
        const day = line.slice(0, idx).trim();
        const time = line.slice(idx).trim();
        if (day && time) return { day, time };
      }

      // "Sön Stängt"
      const m2 = line.match(/^(.+?\b)\s+(.+)$/);
      if (m2 && dayWords.test(m2[1])) return { day: m2[1].trim(), time: m2[2].trim() };

      return null;
    })
    .filter(Boolean);
};

const isToday = (label) => {
  const today = new Date().getDay(); // 0–6
  const map = ["sön", "mån", "tis", "ons", "tor", "fre", "lör"];
  const t = map[today];
  const s = (label || "").toLowerCase();

  if (s.includes(t)) return true;

  // intervall "mån–fre"
  const m = s.match(/(mån|tis|ons|tor|fre|lör|sön)\s*[–-]\s*(mån|tis|ons|tor|fre|lör|sön)/);
  if (!m) return false;

  const order = ["mån", "tis", "ons", "tor", "fre", "lör", "sön"];
  const a = order.indexOf(m[1]);
  const b = order.indexOf(m[2]);
  const x = order.indexOf(t);

  if (a === -1 || b === -1 || x === -1) return false;
  if (a <= b) return x >= a && x <= b;
  return x >= a || x <= b;
};

/* ---------- Page ---------- */

const OpeningHoursPage = () => {
  const [loading, setLoading] = useState(true);

  // Firestore fields: centrum, folketsPark, content
  const [data, setData] = useState({ centrum: "", folketsPark: "", content: "" });

  // vilken flik som är vald
  const [active, setActive] = useState("folketsPark");

  const yearLabel = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    const fetchOpeningHours = async () => {
      try {
        setLoading(true);
        const docRef = doc(firestore, "settings", "openingHours");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const d = docSnap.data() || {};
          setData({
            centrum: d.centrum || "",
            folketsPark: d.folketsPark || "",
            content: d.content || "",
          });
        } else {
          setData({ centrum: "", folketsPark: "", content: "" });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOpeningHours();
  }, []);

  const activeHtml = useMemo(() => {
    const raw = active === "centrum" ? data.centrum : data.folketsPark;
    return sanitize(raw);
  }, [active, data]);

  const rows = useMemo(() => extractRows(activeHtml), [activeHtml]);

  const safeContentHtml = useMemo(() => sanitizeHtmlMinimally(data.content), [data.content]);

  return (
    <>
      <SEO
        title="Öppettider"
        description="Öppettider och information för besök hos Hofors BGK."
        keywords="öppettider, information, hofors bgk, bangolf, minigolf"
      />

      <Page>
        <Container>
          <Hero>
            <div>
              <Title>Öppettider {yearLabel}</Title>
              <Subtitle>Här finns öppettider och extra information inför ditt besök.</Subtitle>
            </div>
            <Actions>
              <BtnGhost to="/">Tillbaka till startsidan</BtnGhost>
            </Actions>
          </Hero>

          <Grid>
            {/* Vänster: Öppettider + content */}
            <Card>
              <CardHeader>
                <CardTitle>Öppettider</CardTitle>
                <Badge>Uppdateras löpande</Badge>
              </CardHeader>

              <Body>
                {loading ? (
                  <Skeleton />
                ) : (
                  <>
                    <ToggleRow>
                      <ToggleBtn
                        type="button"
                        $active={active === "folketsPark"}
                        onClick={() => setActive("folketsPark")}
                      >
                        ⛳ Äventyrsgolf
                      </ToggleBtn>
                      <ToggleBtn
                        type="button"
                        $active={active === "centrum"}
                        onClick={() => setActive("centrum")}
                      >
                        🏌️ Bangolf
                      </ToggleBtn>
                    </ToggleRow>

                    {rows.length ? (
                      <HoursList>
                        {rows.map((r, i) => {
                          const TodayRow = isToday(r.day) ? HoursRowToday : HoursRow;
                          return (
                            <TodayRow key={i}>
                              <Day>{r.day}</Day>
                              <Time>
                                {r.time}
                                {isToday(r.day) && <TodayBadge>Idag</TodayBadge>}
                              </Time>
                            </TodayRow>
                          );
                        })}
                      </HoursList>
                    ) : (
                      <Text>
                        Inga öppettider för <b>{active === "centrum" ? "Bangolf" : "Äventyrsgolf"}</b>.
                      </Text>
                    )}

                    {/* content (HTML) */}
                    {safeContentHtml ? (
                      <>
                        <div style={{ height: 18 }} />
                        <CardHeader style={{ padding: 0, borderBottom: "none" }}>
                          <CardTitle>Information</CardTitle>
                        </CardHeader>
                        <div style={{ height: 10 }} />
                        <Html dangerouslySetInnerHTML={{ __html: safeContentHtml }} />
                      </>
                    ) : null}
                  </>
                )}
              </Body>
            </Card>

            {/* Höger: Extra boxes */}
            <div style={{ display: "grid", gap: 14 }}>
              <Card>
                <CardHeader>
                  <CardTitle>Bra att veta</CardTitle>
                </CardHeader>
                <Body>
                  <Text>
                    <ul>
                      <li>Tider kan ändras vid regn, tävlingar eller arrangemang.</li>
                      <li>Kom gärna i god tid om ni är en större grupp.</li>
                      <li>Vid frågor: kontakta oss innan ni åker.</li>
                    </ul>
                  </Text>
                </Body>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Hitta hit</CardTitle>
                </CardHeader>
                <Body>
                  <Text>
                    <p><b>Adress:</b> Skolgatan 21</p>
                    <p>
                      <a href="https://maps.app.goo.gl/9k9sEWBrFEJcDHjM6" target="_blank" rel="noopener noreferrer">
                        Öppna i Google Maps
                      </a>
                    </p>
                  </Text>
                </Body>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Kontakt</CardTitle>
                </CardHeader>
                <Body>
                  <Text>
                    <p><b>E-post:</b> 070 446 33 02</p>
                    <p><b>Telefon:</b>Hoforsbangolf@gmail.com</p>
                  </Text>
                </Body>
              </Card>
            </div>
          </Grid>
        </Container>
      </Page>
    </>
  );
};

export default OpeningHoursPage;

